import * as React from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Alert,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { BaseModal } from "./global";
import { useCardPreferences } from "../hooks/useCardPreferences";
import { CARD_PREFERENCE_TYPE, CARD_PREFERENCE_PAGE } from "../constants/cardPreferences";
import { DASHBOARD_CARD_IDS, DASHBOARD_CARD_LABELS, DEFAULT_DASHBOARD_CARD_ORDER, JOB_COUNT_CARD_IDS, JOB_COUNT_CARD_LABELS, DEFAULT_JOB_COUNT_ORDER } from "../constants/dashboardCards";
import { PROJECT_CARD_IDS, PROJECT_CARD_LABELS, DEFAULT_PROJECT_CARD_ORDER } from "../constants/projectCards";

interface SortableItemProps {
  id: string;
  label: string;
  isHidden: boolean;
  onToggleVisibility: (id: string) => void;
  showDetailButton?: boolean;
  onDetailClick?: (id: string) => void;
  isDetailExpanded?: boolean;
  children?: React.ReactNode;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  label,
  isHidden,
  onToggleVisibility,
  showDetailButton = false,
  onDetailClick,
  isDetailExpanded = false,
  children,
}) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <ListItem
        ref={setNodeRef}
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 1.5,
          px: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.02)",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.04)",
          },
          position: "relative",
        }}
      >
        <IconButton
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            touchAction: "none",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <DragIndicatorIcon />
        </IconButton>
        <Typography
          variant="body1"
          sx={{
            flex: 1,
            fontWeight: isHidden ? 400 : 500,
            color: isHidden ? "text.disabled" : "text.primary",
            textDecoration: isHidden ? "line-through" : "none",
          }}
        >
          {label}
        </Typography>
        {showDetailButton && onDetailClick && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDetailClick(id);
            }}
            size="small"
            sx={{ mr: 0.5 }}
          >
            {isDetailExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleVisibility(id);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          sx={{
            color: isHidden ? "text.disabled" : "#F4A259",
            pointerEvents: "auto !important",
            position: "relative",
            zIndex: 100,
            "&:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.1)",
              color: "#F4A259",
            },
          }}
        >
          {isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </ListItem>
      {children}
    </>
  );
};

interface CardCustomizeLayoutModalProps {
  open: boolean;
  onClose: () => void;
  type: typeof CARD_PREFERENCE_TYPE.ADMIN | typeof CARD_PREFERENCE_TYPE.CUSTOMER;
  page: typeof CARD_PREFERENCE_PAGE.DASHBOARD | typeof CARD_PREFERENCE_PAGE.PROJECT;
}

const CardCustomizeLayoutModal: React.FC<CardCustomizeLayoutModalProps> = ({
  open,
  onClose,
  type,
  page,
}) => {
  const theme = useTheme();
  const { preferences, isLoading, isSaving, savePreferences, isEmpty, companyId, refetch } = useCardPreferences({
    type,
    page,
    skip: !open,
  });

  // Get card constants based on page - memoized to prevent infinite loops
  const cardIds = React.useMemo(
    () => page === CARD_PREFERENCE_PAGE.DASHBOARD ? DASHBOARD_CARD_IDS : PROJECT_CARD_IDS,
    [page]
  );
  const cardLabels = React.useMemo(
    () => page === CARD_PREFERENCE_PAGE.DASHBOARD ? DASHBOARD_CARD_LABELS : PROJECT_CARD_LABELS,
    [page]
  );
  const defaultCardOrder = React.useMemo<string[]>(
    () => page === CARD_PREFERENCE_PAGE.DASHBOARD 
      ? [...DEFAULT_DASHBOARD_CARD_ORDER] 
      : [...DEFAULT_PROJECT_CARD_ORDER],
    [page]
  );

  const [cardOrder, setCardOrder] = React.useState<string[]>(defaultCardOrder);
  const [hiddenCards, setHiddenCards] = React.useState<string[]>([]);
  const [jobCountOrder, setJobCountOrder] = React.useState<string[]>(DEFAULT_JOB_COUNT_ORDER);
  const [hiddenJobCount, setHiddenJobCount] = React.useState<string[]>([]);
  const [expandedJobCountDetail, setExpandedJobCountDetail] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize state from preferences API
  React.useEffect(() => {
    if (!open) return;

    if (preferences) {
      const allCardIds = Object.values(cardIds);
      
      // Filter cardOrder to only include valid card IDs
      const filteredOrder = (preferences.cardOrder || [...defaultCardOrder]).filter(
        (id: string) => allCardIds.includes(id as any)
      );
      
      // Add any missing cards from default order
      const missingCards = [...defaultCardOrder].filter(
        (id: string) => !filteredOrder.includes(id)
      );
      const finalOrder = [...filteredOrder, ...missingCards];
      
      // Filter hiddenCards to only include valid card IDs
      const filteredHidden = (preferences.hiddenCards || []).filter(
        (id: string) => allCardIds.includes(id as any)
      );

      setCardOrder(finalOrder);
      setHiddenCards(filteredHidden);

      // Handle job count details if it's dashboard page and jobCount card exists
      // Note: jobCount card is not in current dashboard, so this is disabled
      // Code kept for future use if jobCount card is added back
      // if (page === CARD_PREFERENCE_PAGE.DASHBOARD && preferences.details?.jobCount) {
      //   const jobCountDetails = preferences.details.jobCount;
      //   const allJobCountIds = Object.values(JOB_COUNT_CARD_IDS);
      //   
      //   const filteredJobOrder = (jobCountDetails.jobCountOrder || [...DEFAULT_JOB_COUNT_ORDER]).filter(
      //     (id: string) => allJobCountIds.includes(id as any)
      //   );
      //   const missingJobSections = [...DEFAULT_JOB_COUNT_ORDER].filter(
      //     (id: string) => !filteredJobOrder.includes(id)
      //   );
      //   const finalJobOrder = [...filteredJobOrder, ...missingJobSections];
      //   
      //   const filteredHiddenJob = (jobCountDetails.hiddenJobCount || []).filter(
      //     (id: string) => allJobCountIds.includes(id as any)
      //   );
      //
      //   setJobCountOrder(finalJobOrder);
      //   setHiddenJobCount(filteredHiddenJob);
      // }
    } else {
      setCardOrder([...defaultCardOrder]);
      setHiddenCards([]);
      setJobCountOrder([...DEFAULT_JOB_COUNT_ORDER]);
      setHiddenJobCount([]);
    }
  }, [preferences, open, page, cardIds, defaultCardOrder]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleVisibility = (cardId: string) => {
    setHiddenCards((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleJobCountDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setJobCountOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleJobCountVisibility = (sectionId: string) => {
    setHiddenJobCount((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleSave = async () => {
    try {
      // For customer type, companyId is required
      if (type === CARD_PREFERENCE_TYPE.CUSTOMER && !companyId) {
        alert("Company ID is required. Please make sure you are logged in with a company account.");
        return;
      }

      const details: Record<string, any> = {};
      
      // Add job count details if it's dashboard page and jobCount card exists
      // Note: jobCount card is not in current dashboard, so this is disabled
      // if (page === CARD_PREFERENCE_PAGE.DASHBOARD) {
      //   details.jobCount = {
      //     jobCountOrder,
      //     hiddenJobCount,
      //   };
      // }

      await savePreferences(
        cardOrder,
        hiddenCards,
        details
      );
      // Refetch preferences after successful save
      await refetch();
      onClose();
    } catch (error: any) {
      console.error("Failed to save preferences:", error);
      const errorMessage = error?.message || "Failed to save preferences. Please try again.";
      alert(errorMessage);
    }
  };

  const handleReset = () => {
    setCardOrder(defaultCardOrder);
    setHiddenCards([]);
    setJobCountOrder(DEFAULT_JOB_COUNT_ORDER);
    setHiddenJobCount([]);
    setExpandedJobCountDetail(false);
  };

  const handleJobCountDetailClick = (cardId: string) => {
    // Disabled: jobCount card not in current dashboard
    // Code kept for future use if jobCount card is added back
    // if (cardId === "jobCount" && page === CARD_PREFERENCE_PAGE.DASHBOARD) {
    //   setExpandedJobCountDetail((prev) => !prev);
    // }
  };

  const footer = (
    <>
      <Button
        onClick={handleReset}
        disabled={isSaving}
        startIcon={<RestartAltIcon />}
        sx={{
          color: "#F4A259",
          "&:hover": {
            backgroundColor: "rgba(244, 162, 89, 0.1)",
          },
        }}
      >
        Reset to Default
      </Button>
      <Button
        onClick={onClose}
        disabled={isSaving}
        startIcon={<CloseIcon />}
        sx={{
          color: "#F4A259",
          "&:hover": {
            backgroundColor: "rgba(244, 162, 89, 0.1)",
          },
        }}
      >
        Cancel
      </Button>
      <LoadingButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSave();
        }}
        variant="contained"
        loading={isSaving}
        disabled={type === CARD_PREFERENCE_TYPE.CUSTOMER && !companyId}
        sx={{
          backgroundColor: "#F4A259",
          pointerEvents: "auto",
          "&:hover": {
            backgroundColor: "#e6893d",
          },
          "&:disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
            color: "rgba(0, 0, 0, 0.26)",
          },
        }}
      >
        Save Changes
      </LoadingButton>
    </>
  );

  const pageTitle = page === CARD_PREFERENCE_PAGE.DASHBOARD 
    ? "Customize Dashboard Layout" 
    : "Customize Project Layout";

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={pageTitle}
      footer={footer}
      maxWidth="md"
      useGlassCard={false}
    >
      <Box sx={{ p: 3 }}>
        {type === CARD_PREFERENCE_TYPE.CUSTOMER && !companyId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Company ID is required to save preferences. Please make sure you are logged in with a company account.
          </Alert>
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Drag and drop cards to reorder them. Click the eye icon to show or hide cards.
        </Typography>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={cardOrder}
            strategy={verticalListSortingStrategy}
          >
            <List sx={{ p: 0 }}>
              {cardOrder
                .filter((cardId) => cardLabels[cardId])
                .map((cardId, index, filteredArray) => (
                  <React.Fragment key={cardId}>
                    <SortableItem
                      id={cardId}
                      label={cardLabels[cardId] || cardId}
                      isHidden={hiddenCards.includes(cardId)}
                      onToggleVisibility={handleToggleVisibility}
                      showDetailButton={false}
                      onDetailClick={handleJobCountDetailClick}
                      isDetailExpanded={false}
                    >
                      {false && (
                        <Box sx={{ pl: 4, pr: 2, pt: 2, pb: 2 }}>
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Job Count Sections
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                              Customize which job status metrics are shown inside the Job Count card.
                            </Typography>
                          </Stack>
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleJobCountDragEnd}
                          >
                            <SortableContext
                              items={jobCountOrder}
                              strategy={verticalListSortingStrategy}
                            >
                              <List sx={{ p: 0 }}>
                                {jobCountOrder
                                  .filter((sectionId) => JOB_COUNT_CARD_LABELS[sectionId])
                                  .map((sectionId, sectionIndex, sectionFilteredArray) => (
                                    <React.Fragment key={sectionId}>
                                      <SortableItem
                                        id={sectionId}
                                        label={JOB_COUNT_CARD_LABELS[sectionId] || sectionId}
                                        isHidden={hiddenJobCount.includes(sectionId)}
                                        onToggleVisibility={handleToggleJobCountVisibility}
                                      />
                                      {sectionIndex < sectionFilteredArray.length - 1 && (
                                        <Divider sx={{ mx: 2 }} />
                                      )}
                                    </React.Fragment>
                                  ))}
                              </List>
                            </SortableContext>
                          </DndContext>
                        </Box>
                      )}
                    </SortableItem>
                    {index < filteredArray.length - 1 && <Divider sx={{ mx: 2 }} />}
                  </React.Fragment>
                ))}
            </List>
          </SortableContext>
        </DndContext>
      </Box>
    </BaseModal>
  );
};

export default CardCustomizeLayoutModal;

